import { Form, FormikProvider, useFormik } from "formik";
import { CloseTemplateCreateSchema } from "@common/schemas/close-template.schema";
import { CloseTemplate } from "@common/types/closeTemplate";
import { useOrganizationServices } from "@api/organizationService.api";
import {
    CloseTemplateUpdateValidationError,
    useUpdateCloseTemplateMutation,
} from "@api/closeTemplate.api";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Drawer } from "@design-system/Drawer";
import { HStack } from "@design-system/HStack/HStack";
import { CloseTemplateDetailsFields } from "./CloseTemplateDetailsFields";
import { CloseTemplateFormValues } from "./closeTemplateForm.constants";
import styles from "./CloseTemplateFormModal.module.scss";

interface Props {
    open: boolean;
    closeTemplate: CloseTemplate | null;
    onClose: () => void;
}

export const EditCloseTemplateDetailsDrawer = ({
    open,
    closeTemplate,
    onClose,
}: Props) => {
    const { toast: successToast, error: errorToast } = useToaster();
    const { data: services } = useOrganizationServices();
    const serviceList = services ?? [];
    const updateCloseTemplate = useUpdateCloseTemplateMutation(
        closeTemplate ?? undefined,
    );

    const isSubmitting = updateCloseTemplate.isPending;

    const resolvedServiceId = closeTemplate?.serviceId ?? null;

    const formik = useFormik<CloseTemplateFormValues>({
        initialValues: {
            serviceId: resolvedServiceId,
            name: closeTemplate?.name ?? "",
            ownerId: closeTemplate?.ownerId ?? null,
            basis: closeTemplate?.basis ?? null,
            industries: closeTemplate?.industries ?? [],
            entityLegalTypes: closeTemplate?.entityLegalTypes ?? [],
        },
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(CloseTemplateCreateSchema),
        validateOnMount: true,
        onSubmit: submitHelper({
            loading: isSubmitting,
            handler: async (values, _formikHelpers) => {
                const payload = {
                    serviceId: values.serviceId,
                    name: values.name.trim(),
                    ownerId: values.ownerId ?? null,
                    basis: values.basis ?? null,
                    industries: values.industries ?? [],
                    entityLegalTypes: values.entityLegalTypes ?? [],
                };
                await updateCloseTemplate.mutateAsync(payload);
                successToast(`Updated template "${payload.name}"`);
                onClose();
            },
            failureHandler: async (error, _values, formikHelpers) => {
                if (error instanceof CloseTemplateUpdateValidationError) {
                    void formikHelpers.setFieldError("basis", error.message);
                    return;
                }
                errorToast(
                    `Failed to update template "${formik.values.name.trim()}"`,
                );
            },
        }),
    });

    return (
        <Drawer
            open={open}
            onClose={onClose}
            direction="right"
            size="fullScreen"
            noPadding
            headerAddonLeft="Edit template"
        >
            {closeTemplate === null ? null : (
                <div className={styles.formWrapper}>
                    <FormikProvider value={formik}>
                        <Form onSubmit={formik.handleSubmit}>
                            <CloseTemplateDetailsFields
                                serviceList={serviceList}
                                footer={
                                    <HStack as="footer" justify="end" gap={12}>
                                        <Button
                                            variant="secondary"
                                            size="xl"
                                            type="button"
                                            onClick={onClose}
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </Button>
                                        <ButtonWithLoader
                                            variant="primary"
                                            size="xl"
                                            type="submit"
                                            loading={isSubmitting}
                                            disabled={!formik.isValid}
                                            hotKey="mod+enter"
                                            hotKeyDisplayPosition="inline"
                                            hotKeyOptions={{
                                                description: "Save template",
                                                preventDefault: true,
                                                enableOnFormTags: true,
                                            }}
                                        >
                                            Save
                                        </ButtonWithLoader>
                                    </HStack>
                                }
                            />
                        </Form>
                    </FormikProvider>
                </div>
            )}
        </Drawer>
    );
};
